import {
  ApplicationConfig,
  ErrorHandler,
  LOCALE_ID,
  inject,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  provideZonelessChangeDetection,
} from '@angular/core';
import {
  provideRouter,
  Router,
  withComponentInputBinding,
  withViewTransitions,
} from '@angular/router';
import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { providePrimeNG } from 'primeng/config';
import { MessageService } from 'primeng/api';
import { EuropaPreset } from './core/theme/europa-preset';
import { provideMatomo, withRouter } from 'ngx-matomo-client';
import { registerLocaleData } from '@angular/common';
import localeFr from '@angular/common/locales/fr';
import * as Sentry from '@sentry/angular';
import { routes } from './app.routes';
import { authInterceptor } from './core/interceptors/auth.interceptor';
import { errorInterceptor } from './core/interceptors/error.interceptor';
import { AuthService } from './core/services/auth.service';
import { provideEcharts } from './core/charts/echarts-loader.service';
import { environment } from '../environments/environment';

registerLocaleData(localeFr);

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideZonelessChangeDetection(),
    provideRouter(routes, withComponentInputBinding(), withViewTransitions()),
    provideHttpClient(withFetch(), withInterceptors([authInterceptor, errorInterceptor])),
    provideAnimationsAsync(),
    providePrimeNG({ theme: { preset: EuropaPreset } }),
    // Toasts globaux (PrimeNG) : singleton injectable partout, host <p-toast> à la racine.
    MessageService,
    // echarts chargé en lazy chunk (tree-shake), hors du bundle initial, par un chargeur unique
    // (cf. EchartsLoaderService : un graphe détruit pendant le chargement ne doit pas naître).
    provideEcharts(),
    { provide: LOCALE_ID, useValue: 'fr-FR' },

    // Matomo uniquement en production (décision projet) : matomoUrl vide hors prod → pas de provider,
    // donc aucun appel réseau ni script tiers chargé en local/dev/preprod.
    ...(environment.matomoUrl
      ? [
          provideMatomo(
            { trackerUrl: environment.matomoUrl, siteId: environment.matomoSiteId },
            withRouter(),
          ),
        ]
      : []),

    // Sentry : ErrorHandler global + instrumentation router (OK en zoneless).
    { provide: ErrorHandler, useValue: Sentry.createErrorHandler() },
    { provide: Sentry.TraceService, deps: [Router] },
    provideAppInitializer(() => {
      inject(Sentry.TraceService);
      // Hydrate la session au démarrage ; échec silencieux => le guard renvoie au login.
      return inject(AuthService).loadSession();
    }),
  ],
};
